import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal'; // install via: npm install react-modal

const ImageCapture = ({
    isOpen,
    onClose,
    onCapture,
    label = ' Capture Document',
}) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);

    const [stream, setStream] = useState(null);
    const [capturedImage, setCapturedImage] = useState(null);
    const [isCaptured, setIsCaptured] = useState(false);
    const [videoKey, setVideoKey] = useState(Date.now());
    const [cameraReady, setCameraReady] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) startCamera();

        return () => {
            stopCamera();
            setStream(null);
            setCameraReady(false);
        };
    }, [isOpen]);

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: 'environment' },
                audio: false,
            });

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    videoRef.current.play();
                    setCameraReady(true);
                };
            }

            setStream(stream);
        } catch (err) {
            alert('❌ Camera access denied. Please check browser permissions.');
            onClose?.();
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach((track) => track.stop());
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setCameraReady(false);
    };

    const captureImage = () => {
        const video = videoRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageDataUrl = canvas.toDataURL('image/jpeg');
        stopCamera();
        setCapturedImage(imageDataUrl);
        setIsCaptured(true);
    };

    const retakeImage = () => {
        setIsCaptured(false);
        setCapturedImage(null);
        setVideoKey(Date.now());
        startCamera();
    };

    function resizeBase64To5MB(base64Data) {
        return new Promise((resolve, reject) => {
            const sizeInBytes = (base64Data.length * (3 / 4)) -
                (base64Data.endsWith('==') ? 2 : base64Data.endsWith('=') ? 1 : 0);
            const sizeInMB = sizeInBytes / (1024 * 1024);

            if (sizeInMB <= 5) return resolve(base64Data);

            const img = new Image();
            img.onload = () => {
                const scaleFactor = Math.sqrt(5 / sizeInMB);
                const canvas = document.createElement('canvas');
                canvas.width = img.width * scaleFactor;
                canvas.height = img.height * scaleFactor;

                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
                resolve(resizedBase64);
            };
            img.onerror = () => reject(new Error('Invalid base64 image data'));
            img.src = base64Data;
        });
    }

    const saveImage = async () => {
        if (capturedImage && onCapture) {
            setIsSaving(true);
            try {
                const base64 = await resizeBase64To5MB(capturedImage);
                await onCapture(base64);
                retakeImage(); // Reset to camera for next capture
            } finally {
                setIsSaving(false);
            }
        }
    };

    const handleClose = () => {
        stopCamera();
        setCapturedImage(null);
        setIsCaptured(false);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onRequestClose={handleClose} ariaHideApp={false}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div class="modal-header p-2">
                        <h4 className="modal-title">{label}</h4>
                        <button
                            type="button"
                            className="btn-close btn-close-white text-white"
                            onClick={handleClose}
                        // style={{ background: "#eee", borderRadius: "4px" }}
                        >
                            {/* <span aria-hidden="true" className="pop-cancel">&times;</span> */}
                        </button>
                    </div>

                    <div className="modal-body text-center">
                        {!isCaptured && (
                            <>
                                {!cameraReady && <p>Loading camera...</p>}
                                <video
                                    key={videoKey}
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    muted
                                    style={{
                                        width: '100%',
                                        border: '1px solid #ccc',
                                        display: cameraReady ? 'block' : 'none',
                                    }}
                                />
                            </>
                        )}

                        <canvas ref={canvasRef} style={{ display: 'none' }} />

                        {isCaptured && capturedImage && (
                            <img
                                src={capturedImage}
                                alt="Captured Document"
                                style={{
                                    width: '100%',
                                    border: '1px solid #ccc',
                                }}
                            />
                        )}
                    </div>

                    <div className="modal-footer">
                        {!isCaptured ? (
                            <button
                                className="btn btn-aqua"
                                onClick={captureImage}
                                disabled={!cameraReady || isSaving}
                            >
                                Capture
                            </button>
                        ) : (
                            <button
                                className="btn btn-warning"
                                onClick={retakeImage}
                                disabled={isSaving}
                            >
                                Retake
                            </button>
                        )}

                        {isCaptured && (
                            <button
                                className="btn btn-primary"
                                onClick={saveImage}
                                disabled={isSaving}
                            >
                                {isSaving ? 'Saving...' : 'Save'}
                            </button>
                        )}

                        <button
                            className="btn btn-gray"
                            onClick={handleClose}
                            disabled={isSaving}
                        >
                            Back
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ImageCapture;
